// Deterministic ingredient parsing for the recipe importer, in every language
// lib/recipeWords.ts knows (English, Swedish, German, Norwegian, Danish, French,
// Italian, Spanish) and whatever else writes amounts with Latin digits. The
// `quantity` field on an Item is an opaque free-text string (see supabase.ts),
// so all amount/unit math lives here: parse a raw ingredient line into an
// amount + unit + name, scale by portions, merge amounts into an existing item,
// and re-serialise back to a display string. Pure functions — unit-tested.

import type { Item } from './supabase'
import { fold, isAllPrep, stripDescriptors } from './normalize'
import { isSureMatch, rankedMatches } from './search'
import { userLocale } from './locale'
import {
  ABOUT_WORDS, ALTERNATIVE_WORDS as ALTERNATIVES, ARTICLE_WORDS, ELIDED_OF_WORDS, OF_WORDS, PART_WORDS, RANGE_WORDS,
  UNIT_SPELLINGS, YIELD_LINKS, YIELD_WORDS, anyOf,
} from './recipeWords'

export type IngredientIssue = 'alternative' | 'multi-item' | 'amount'

export interface ParsedIngredient {
  raw: string
  amount: number | null
  unit: string | null
  name: string
  alternatives?: string[] // "X eller Y" → pick one
  parts?: string[]        // "salt och peppar" → optionally split into many
  issues: IngredientIssue[]
}

export interface Amount {
  amount: number
  unit: string | null
}

// Looked up by folded spelling, so "forp" typed without the ö still counts, and
// without a final full stop, as the line's token is ("c. à s." / "c. à s").
const unitKey = (s: string) => fold(s).replace(/\.$/, '')
const UNIT_ALIASES: Record<string, string> = Object.fromEntries(
  Object.entries(UNIT_SPELLINGS).map(([k, v]) => [unitKey(k), v]),
)
/** The longest unit in words: "cuillère à soupe". */
const UNIT_MAX_TOKENS = Math.max(...Object.keys(UNIT_SPELLINGS).map(k => k.split(' ').length))

// Words that join two ingredients on one line (lib/recipeWords.ts). Alternatives
// ("X or Y") are auto-resolved to the first; parts ("salt and pepper") are
// offered as a split.
const ALTERNATIVE_WORDS = new RegExp(`\\s+(?:${anyOf(ALTERNATIVES)})\\s+`, 'i')
const PARTS_WORDS = new RegExp(`\\s+(?:${anyOf(PART_WORDS)})\\s+`, 'i')

// Names that contain "and" but are one thing — checked before splitting.
const COMPOUND_NAMES = [
  'half and half', 'mac and cheese', 'macaroni and cheese', 'sweet and sour',
  'bread and butter', 'fish and chips', 'salt and vinegar', 'surf and turf',
  'peanut butter and jelly', 'pork and beans',
]

// "red and green peppers" is one line about peppers, not two items.
const BARE_QUALIFIERS = new Set(['red', 'green', 'yellow', 'white', 'black', 'sweet', 'hot', 'mild',
  'rod', 'gron', 'gul', 'vit', 'svart'])

const FRACTIONS: Record<string, number> = {
  '½': 0.5, '¼': 0.25, '¾': 0.75, '⅓': 1 / 3, '⅔': 2 / 3, '⅛': 0.125,
}

// ── ingredient parsing ────────────────────────────────────────────────────────

// "Juice of 1 lemon", "Saften av 1 citron": the thing to buy is the lemon.
const YIELD_OF = new RegExp(`^(?:${anyOf(YIELD_WORDS)})\\s+(?:${anyOf(YIELD_LINKS)})\\s+`, 'i')
// "A pinch of nutmeg", "en gul lök", "eine Prise Salz": an article at the start means one.
const ARTICLE = new RegExp(`^(?:${anyOf(ARTICLE_WORDS)})\\s+`, 'i')
// "1 cup of flour", "1 burk av …": the "of" belongs to the unit.
const OF = new RegExp(`^(?:(?:${anyOf(OF_WORDS)})\\s+|${anyOf(ELIDED_OF_WORDS)})`, 'i')
const RANGE = new RegExp(`^(\\d+)\\s*(?:[-–]|\\s(?:${anyOf(RANGE_WORDS)})\\s)\\s*(\\d+)\\b\\s*(.*)$`, 'i')
// "ca. 200 g", "about 2 cups": an approximate amount is still the amount.
const ABOUT = new RegExp(`^(?:${anyOf(ABOUT_WORDS)})\\s+(?=\\d|[½¼¾⅓⅔⅛])`, 'i')

export function parseIngredient(raw: string): ParsedIngredient {
  // Parentheticals go first — "1 (14.5 ounce) can tomatoes" is one can, and the
  // size in brackets would otherwise sit between the amount and its unit.
  const cleaned = raw
    .replace(/\([^)]*\)/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(YIELD_OF, '')
    .replace(ABOUT, '')
    .replace(ARTICLE, '1 ')
  const issues: IngredientIssue[] = []

  const parsed = parseLeadingAmount(cleaned)
  if (parsed.range) issues.push('amount')
  const rest = parsed.rest
  let amount = parsed.amount

  // Unit is the first token (or two — "fl oz") after the amount, if it's a
  // known unit word; a following "of" ("1 cup of flour") goes with it. An
  // alternate measurement after a slash ("500g/1lb 2oz") is dropped, and
  // "2 x 400g cans" keeps the count and the container.
  let unit: string | null = null
  let name = rest
  if (amount != null) {
    const measure = parseMeasure(amount, rest)
    amount = measure.amount
    unit = measure.unit
    name = measure.rest.replace(OF, '')
  } else {
    // "handful of parsley", "pinch of salt": a unit with no number is one of it.
    const found = leadingUnit(rest)
    if (found && OF.test(found.rest)) {
      amount = 1
      unit = found.unit
      name = found.rest.replace(OF, '')
    }
  }

  // Splitting happens before descriptors are stripped, so "fresh or dried
  // thyme" loses its "fresh" per side rather than leaving "or dried thyme".
  // "1 gul lök, finhackad" → "gul lök": the shopping list wants the thing to
  // buy, not the prep instruction; `raw` keeps the original line.
  const result: ParsedIngredient = { raw, amount, unit, name: stripDescriptors(name), issues }

  // "X eller Y" / "X or Y" → alternatives to choose between. A side that is
  // only prep words ("fresh or dried thyme") is a qualifier, not a choice: the
  // thing to buy is thyme.
  if (ALTERNATIVE_WORDS.test(name)) {
    const sides = name.split(ALTERNATIVE_WORDS).map(s => s.trim()).filter(Boolean)
    const real = sides.filter(s => !isAllPrep(s))
    if (real.length < sides.length) {
      result.name = stripDescriptors(real.join(' ')) || result.name
    } else if (sides.length >= 2) {
      result.alternatives = sides.map(s => stripDescriptors(s))
      result.name = result.alternatives[0]
      issues.push('alternative')
    }
  }

  // "salt och peppar" / "salt and pepper" → two simple items (only when both
  // sides look like a short standalone ingredient, so "kött och fläskfärs"
  // style compound names aren't wrongly split, and never for a name that is
  // one thing with an "and" in it). Split on the descriptor-stripped name, so
  // the "and" of "potatoes, peeled and cubed" is gone before this looks.
  if (!result.alternatives && !isCompoundName(result.name)) {
    const parts = result.name.split(PARTS_WORDS).map(s => stripDescriptors(s.trim())).filter(Boolean)
    if (parts.length === 2 && parts.every(isSimpleName) && !BARE_QUALIFIERS.has(fold(parts[0]))) {
      result.parts = parts
      issues.push('multi-item')
    }
  }

  return result
}

/**
 * "salt, svartpeppar", "Salz, Pfeffer & Paprikapulver": a few things with no
 * amount on one line, as a magazine sets them — each its own item. Null for
 * anything else, so "persilja, hackad" stays one thing with its prep dropped.
 */
export function bareList(raw: string): string[] | null {
  const line = raw.replace(/\([^)]*\)/g, ' ').replace(/\s+/g, ' ').trim()
  if (!line.includes(',') || parseLeadingAmount(line).amount != null) return null
  const items = line.split(/\s*,\s*/)
    .flatMap((s, i, all) => (i === all.length - 1 ? s.split(PARTS_WORDS) : [s]))
    .map(s => s.trim())
    .filter(Boolean)
  return items.length >= 2 && items.every(isSimpleName) && !isCompoundName(line) ? items : null
}

// Up to three words ("ground black pepper"), no digits, and not a bare prep
// word — "cubed" is not something to buy.
function isSimpleName(s: string): boolean {
  return s.length > 0 && s.split(' ').length <= 3 && !/\d/.test(s) && !isAllPrep(s)
}

// Reads the unit that follows a leading amount, plus the two shapes English
// sites wrap it in:
//
//   "2 x 400g cans chickpeas"   → 2 can chickpeas   (count + container)
//   "2 x 400g chickpeas"        → 800 g chickpeas   (no container: multiply)
//   "500g/1lb 2oz minced beef"  → 500 g minced beef (alternate measure dropped)
//   "200ml/7fl oz double cream" → 200 ml double cream
function parseMeasure(amount: number, rest: string): { amount: number; unit: string | null; rest: string } {
  const spaced = spaceSlashes(rest)

  const mult = spaced.match(/^[x×]\s+(.*)$/i)
  if (mult) {
    const size = parseLeadingAmount(mult[1])
    if (size.amount != null) {
      const sizeUnit = leadingUnit(spaceSlashes(size.rest))
      if (sizeUnit) {
        const after = dropAlternateMeasure(sizeUnit.rest)
        const container = leadingUnit(after)
        if (container) return { amount, unit: container.unit, rest: container.rest }
        return { amount: amount * size.amount, unit: sizeUnit.unit, rest: after }
      }
      return { amount: amount * size.amount, unit: null, rest: size.rest }
    }
    return { amount, unit: null, rest: mult[1] }
  }

  const found = leadingUnit(spaced)
  if (!found) return { amount, unit: null, rest }
  return { amount, unit: found.unit, rest: dropAlternateMeasure(found.rest) }
}

// "500g/1lb" → "500g / 1lb", so the slash becomes its own token; "1/2" is left
// alone (digits on both sides), as is "salt/pepper" (letters on both sides).
function spaceSlashes(s: string): string {
  return s.replace(/([a-zA-Z.])\/(?=\d)/g, '$1 / ')
}

// Drops a run of "<number><unit>" tokens after a leading slash — the imperial
// (or metric) twin of the measure already read. Stops at the first word that is
// neither a number nor a unit, which is where the ingredient name starts.
function dropAlternateMeasure(rest: string): string {
  if (!rest.startsWith('/')) return rest
  const toks = rest.split(' ').slice(1)
  let i = 0
  while (i < toks.length) {
    const t = toks[i]
    if (/^\d/.test(t) || /^[½¼¾⅓⅔⅛]/.test(t)) { i++; continue }
    const two = i + 1 < toks.length ? UNIT_ALIASES[unitKey(`${t} ${toks[i + 1]}`)] : undefined
    if (two) { i += 2; continue }
    if (UNIT_ALIASES[unitKey(t)]) { i++; continue }
    break
  }
  return toks.slice(i).join(' ').trim()
}

function isCompoundName(s: string): boolean {
  const f = fold(s)
  return COMPOUND_NAMES.some(c => f.includes(c))
}

// Longer units ("c. à soupe", "fl oz") are tried before one-token ones.
function leadingUnit(rest: string): { unit: string; rest: string } | null {
  const toks = rest.split(' ')
  for (let n = UNIT_MAX_TOKENS; n >= 1; n--) {
    if (toks.length < n) continue
    const key = unitKey(toks.slice(0, n).join(' '))
    const unit = UNIT_ALIASES[key]
    if (unit) return { unit, rest: toks.slice(n).join(' ').trim() }
  }
  return null
}

// Parses a leading quantity: integers, decimals (1,5 / 1.5), unicode fractions
// (½, 1 ½), ascii fractions (1/2, 1 1/2), and ranges (2-3, 2 to 3, 2 or 3,
// 2 till 3 → lower bound, flagged).
function parseLeadingAmount(s: string): { amount: number | null; rest: string; range: boolean } {
  // range: "2-3", "2–3", "2 to 3", "2 till 3", "2 or 3", "2 à 3" (lib/recipeWords.ts RANGE_WORDS)
  let m = s.match(RANGE)
  if (m) return { amount: parseInt(m[1], 10), rest: m[3].trim(), range: true }

  // integer + fraction glyph: "1 ½"
  m = s.match(/^(\d+)\s*([½¼¾⅓⅔⅛])\s*(.*)$/)
  if (m) return { amount: parseInt(m[1], 10) + FRACTIONS[m[2]], rest: m[3].trim(), range: false }

  // integer + ascii fraction: "1 1/2"
  m = s.match(/^(\d+)\s+(\d+)\/(\d+)\s*(.*)$/)
  if (m) return { amount: parseInt(m[1], 10) + parseInt(m[2], 10) / parseInt(m[3], 10), rest: m[4].trim(), range: false }

  // lone fraction glyph: "½"
  m = s.match(/^([½¼¾⅓⅔⅛])\s*(.*)$/)
  if (m) return { amount: FRACTIONS[m[1]], rest: m[2].trim(), range: false }

  // ascii fraction: "1/2"
  m = s.match(/^(\d+)\/(\d+)\s*(.*)$/)
  if (m) return { amount: parseInt(m[1], 10) / parseInt(m[2], 10), rest: m[3].trim(), range: false }

  // decimal: "1,5" / "1.5"
  m = s.match(/^(\d+)[.,](\d+)\s*(.*)$/)
  if (m) return { amount: parseFloat(`${m[1]}.${m[2]}`), rest: m[3].trim(), range: false }

  // plain integer: "4"
  m = s.match(/^(\d+)\s*(.*)$/)
  if (m) return { amount: parseInt(m[1], 10), rest: m[2].trim(), range: false }

  return { amount: null, rest: s, range: false }
}

// ── scaling & formatting ──────────────────────────────────────────────────────

export function scaleAmount(amount: number | null, factor: number): number | null {
  if (amount == null) return null
  return amount * factor
}

// The decimal separator follows the device ("1,5" in Swedish, "1.5" in
// English); parseLeadingAmount reads both back, so a quantity written on one
// member's phone parses on another's. No grouping and Latin digits always —
// "1,000 g" would read back as 1, and Arabic-Indic digits wouldn't read at all.
export function formatNumber(n: number, locale: string = userLocale()): string {
  const r = Math.round(n * 100) / 100
  return new Intl.NumberFormat(locale, { maximumFractionDigits: 2, useGrouping: false, numberingSystem: 'latn' }).format(r)
}

export function formatAmount(amount: number | null, unit: string | null, locale: string = userLocale()): string {
  if (amount == null) return ''
  const num = formatNumber(amount, locale)
  return unit ? `${num} ${unit}` : num
}

// ── rounding like a person writes ─────────────────────────────────────────────
//
// Scaling 400 g for 6 instead of 4 servings gives 600 g, but 250 g × 0.75 gives
// 187.5 g, which nobody writes. `roundAmount` rounds the way a shopping list is
// written (ported from the approved prototype's roundQty): grams and millilitres
// in 5/10/25 steps and in kg/l from 1000, decilitres to the half, everything
// else (counts, cans, spoons) to the half below 3 and whole above. The unit stays
// canonical (singular), so the result merges and parses like any other Amount;
// `displayAmount` adds the plural and the ½ glyph for the screen. Every string it
// writes reads back through parseQuantityString (pinned by a test).

const dec = (n: number) => Math.round(n * 10) / 10

export function roundAmount(amount: number, unit: string | null): Amount {
  const u = unit ?? ''
  if ((u === 'g' || u === 'ml') && amount >= 1000) return { amount: dec(amount / 1000), unit: u === 'g' ? 'kg' : 'l' }
  if (u === 'g' || u === 'ml') {
    const x = amount < 50 ? Math.max(5, Math.round(amount / 5) * 5)
      : amount < 200 ? Math.round(amount / 10) * 10
      : Math.round(amount / 25) * 25
    return { amount: x, unit }
  }
  if (u === 'kg' || u === 'l') return { amount: Math.max(0.1, dec(amount)), unit }
  if (u === 'dl') return { amount: Math.max(0.5, Math.round(amount * 2) / 2), unit }
  return { amount: amount < 3 ? Math.max(0.5, Math.round(amount * 2) / 2) : Math.round(amount), unit }
}

// English container/count units read better in the plural above one. Swedish
// units are written the same either way on a list ("3 burk" is how people
// abbreviate it), and abbreviations (g, dl, tbsp) never take one.
const PLURALS: Record<string, string> = {
  can: 'cans', pack: 'packs', clove: 'cloves', bunch: 'bunches', jar: 'jars', slice: 'slices',
  sprig: 'sprigs', stalk: 'stalks', handful: 'handfuls', cup: 'cups', pinch: 'pinches',
}
const METRIC = new Set(['g', 'kg', 'ml', 'cl', 'dl', 'l', 'hg'])

/** "1½ bunches", "600 g", "1,2 l" (decimal separator per locale), "" for no amount. */
export function displayAmount(amount: number | null, unit: string | null, locale: string = userLocale()): string {
  if (amount == null) return ''
  const whole = Math.floor(amount)
  const half = !METRIC.has(unit ?? '') && Math.abs(amount - whole - 0.5) < 1e-9
  const num = half ? (whole === 0 ? '½' : `${whole}½`) : formatNumber(amount, locale)
  if (!unit) return num
  return `${num} ${amount > 1 && PLURALS[unit] ? PLURALS[unit] : unit}`
}

/** An amount scaled by `factor` and rounded — unless nothing was scaled, when it
 *  is shown exactly as written. */
export function scaledAmount(amount: number | null, unit: string | null, factor: number): Amount | null {
  if (amount == null) return null
  return factor === 1 ? { amount, unit } : roundAmount(amount * factor, unit)
}

/** One ingredient's amount at `factor`, for display ("600 g"); "" when it has none.
 *  Per ingredient, never merged by name: the recipe page and cooking mode both list each line. */
export function ingredientAmount(g: { amount: number | null; unit: string | null }, factor: number, locale?: string): string {
  const a = scaledAmount(g.amount, g.unit, factor)
  return a ? displayAmount(a.amount, a.unit, locale) : ''
}

// ── merging into existing items ────────────────────────────────────────────────

// Parses an existing item's free-text quantity ("2 dl", "500g", "2") back into
// an Amount, or null when there's no numeric amount to merge against.
export function parseQuantityString(q: string | null): Amount | null {
  if (!q) return null
  const { amount, rest } = parseLeadingAmount(q.replace(/\s+/g, ' ').trim())
  if (amount == null) return null
  return { amount, unit: leadingUnit(rest)?.unit ?? null }
}

// Sums two amounts when their units are compatible (same canonical unit, or both
// unitless); returns null when they can't be merged (→ surfaced as a conflict).
export function mergeAmounts(a: Amount, b: Amount): Amount | null {
  if (a.unit === b.unit) return { amount: a.amount + b.amount, unit: a.unit }
  return null
}

// ── matching against the current list ──────────────────────────────────────────

// Finds the list item this ingredient name refers to, the way quick add
// matches a typed name (lib/search.ts), taking only a sure match: the same
// name (EN↔SV, plural, word order and descriptors aside) or one slip away. A
// name that merely starts with or contains the other is a different thing —
// "tomatpuré" is not "tomat" — and the choices sheet offers it as "Maybe the
// same as" instead (lib/catalog.ts nameSuggestions).
//
// Items already ticked off are skipped by default: merging into a done item
// would leave the new amount struck through on the list. Those become new items
// instead (they still teach the category memory).
//
// `includeDone` is for the opposite question — "is this ingredient already
// covered?" (recipeMatch.ts). There a ticked-off item is the *best* possible
// answer: it means the thing is already bought.
export function matchExisting(
  name: string,
  items: Item[],
  opts: { includeDone?: boolean; locale?: string } = {},
): Item | null {
  const open = opts.includeDone ? items : items.filter(i => !i.done)
  return rankedMatches(open, name, opts.locale ?? userLocale(), open.length).find(isSureMatch)?.item ?? null
}

